import { FC, useEffect } from 'react';

import { Box, Button, Divider, Stack, SxProps, Theme, Typography } from '@mui/material';
import Scrollbars from 'react-custom-scrollbars-2';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useScrollPercentage } from 'shared/hooks';
import { TCashdesk } from 'shared/services/types/cashdesk.types';
import { CustomScrollbar } from 'shared/ui/custom-scrollbar';
import { FormWrapper } from 'shared/ui/form-wrapper';
import { Input } from 'shared/ui/input';
import { ModalTemplate } from 'shared/ui/modal-template';
import { addMessageToast, hasFormValuesChanged } from 'shared/utils';

interface IProps {
  isOpen: boolean;
  dataInfo?: TCashdesk.CashdeskData;
  dataCreate?: TCashdesk.CreateCashdesk;
  modalType: '' | 'info';
  onClose: () => void;
}

export const AddCashdesk: FC<IProps> = ({ isOpen, dataInfo, dataCreate, modalType, onClose }) => {
  const methods = useForm({ mode: 'onBlur' });
  const { reset } = methods;
  const { handleScroll, scrollbarRef, contentRef, isShowScroll, ...customScrollProps } =
    useScrollPercentage();

  const isConfirmationNeeded = hasFormValuesChanged(methods.watch(), dataCreate ?? {});

  useEffect(() => {
    if (dataCreate) reset(dataCreate);
  }, [dataCreate]);

  const onSubmit: SubmitHandler<TCashdesk.CreateCashdesk> = async data => {
    try {
      if (!dataCreate) {
        addMessageToast('User created', 'success');
      } else {
        addMessageToast('User updated', 'success');
      }
      onClose();
      reset([]);
    } catch (error) {
      addMessageToast(error);
    }
  };

  const InfoRow = (isHead: boolean, column1: string, column2: string | number = '') => {
    return (
      <Stack direction='row' justifyContent='space-between' gap='12px'>
        <Typography
          variant='body1'
          sx={{
            fontSize: isHead ? 16 : 15,
            lineHeight: '20px',
            fontWeight: isHead ? '600' : '500',
            width: 'calc(50% - 6px)'
          }}>
          {column1 + (isHead ? '' : ':')}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            fontSize: isHead ? 16 : 15,
            lineHeight: '20px',
            fontWeight: '600',
            width: 'calc(50% - 6px)'
          }}>
          {column2}
        </Typography>
      </Stack>
    );
  };

  switch (modalType) {
    case 'info':
      return (
        <ModalTemplate
          isConfirmationNeeded={isConfirmationNeeded}
          close={onClose}
          isOpen={isOpen}
          sx={customModalSX}
          titleText={dataInfo?.name ?? ''}
          reset={reset}>
          <Stack gap='12px' sx={{ p: '30px 24px 12px' }}>
            {InfoRow(true, 'Total', 'Count')}
            {InfoRow(false, 'Total agent', dataInfo?.totalAgents)}
            {InfoRow(false, 'Total method', dataInfo?.totalMethods)}
          </Stack>
          <Divider sx={{ m: '0 24px' }} />
          <Box sx={{ position: 'relative', p: '12px 0 30px' }}>
            <Scrollbars
              ref={scrollbarRef}
              autoHeight
              autoHeightMax={170}
              style={{
                width: '100%',
                background: 'var(--mui-palette-background-paper)',
                scrollbarColor: 'transparent transparent'
              }}
              renderTrackVertical={undefined}
              renderThumbVertical={undefined}
              onScroll={handleScroll}>
              <Box
                ref={contentRef}
                sx={{
                  p: '0 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                {InfoRow(true, 'Method name', 'Count')}
                {dataInfo?.methods.map(item => InfoRow(false, item.name, item.count))}
              </Box>
            </Scrollbars>
            {isShowScroll && (
              <CustomScrollbar
                customScrollProps={customScrollProps}
                sxScroll={{ height: 'calc(100% - 44px)', top: '14px', right: '4px', width: '5px' }}
              />
            )}
          </Box>
        </ModalTemplate>
      );
    default:
      return (
        <ModalTemplate
          isConfirmationNeeded={isConfirmationNeeded}
          close={onClose}
          isOpen={isOpen}
          sx={customModalSX}
          titleText={dataCreate ? 'Edit cash desk' : 'Add cash desk'}
          reset={() => {
            reset();
            methods.clearErrors();
          }}
          maxWidth={413}>
          <FormWrapper onSubmit={onSubmit} methods={methods}>
            <Stack gap='30px'>
              <Input
                placeholder='Enter cash desk name'
                label='Cash desk'
                name='name'
                rules={{ required: 'Please, enter cash desk name' }}
              />
              <Input
                placeholder='Enter cash desk ID'
                type='number'
                label='Cash desk ID'
                name='desk_id'
                rules={{ required: 'Please, enter cash desk ID' }}
              />
              <Input
                placeholder='Enter bot ID'
                label='Bot ID'
                name='bot_id'
                rules={{ required: 'Please, enter Bot ID' }}
              />
              <Input
                placeholder='Enter cash desk login'
                label='Cash desk login'
                name='login'
                rules={{ required: 'Please, enter login' }}
              />
              <Input
                placeholder='Enter cash desk password'
                label='Password'
                type='password'
                name='password'
                rules={{ required: 'Please, enter password' }}
              />
              <Input
                placeholder='Enter hash key'
                label='Hash key'
                name='hash_key'
                rules={{ required: 'Please, enter hash key' }}
              />
              <Button
                type='submit'
                fullWidth
                variant='contained'
                disabled={!methods.formState.isValid}
                sx={{
                  m: 0,
                  borderRadius: 50,
                  textTransform: 'none',
                  height: 40,
                  boxShadow: 'none'
                }}>
                Save
              </Button>
            </Stack>
          </FormWrapper>
        </ModalTemplate>
      );
  }
};

const customModalSX = {
  '.modal-title-wrapper': {
    paddingTop: '30px'
  }
} as SxProps<Theme>;
